import { DateTime } from "luxon";

import Filters from "../../../../filters";

import { DisplayStatus } from "./consts";

import type { FormattedFileImportItem } from "./types";

type FormatWireImportBatchOptions = {
  institutionAccount: FormattedFileImportItem["institutionAccount"];
  dualApprovalId: FormattedFileImportItem["dualApprovalId"];
  userId: FormattedFileImportItem["userId"];
};

export const formatWireImportBatchAsFileImportItem = (
  batch: API.WireImport.WireImportBatch,
  options: FormatWireImportBatchOptions,
): FormattedFileImportItem => {
  const { institutionAccount, dualApprovalId, userId } = options;

  const amountCents = batch.total_amount as Cents;
  const netTotalDollars = Filters.currency(amountCents, {
    hasDecimal: false,
  });
  const netTotalDollarsWithSign = `-${netTotalDollars}`;

  return {
    uuid: batch.id,
    type: "wire",
    title: batch.description,
    state: batch.state,
    displayStatus: DisplayStatus[batch.state] || DisplayStatus.unknown,
    institutionAccount,
    organizationFile: batch.file_name
      ? { file_name: batch.file_name, file_type: "csv" }
      : null,
    netTotalDollars,
    netTotalDollarsWithSign,
    dateRange: batch.created_at
      ? DateTime.fromISO(batch.created_at).toFormat("MMM d, yyyy")
      : "",
    paymentCount: batch.wire_count,
    paymentCreditCount: 0,
    paymentDebitCount: 0,
    isExpedited: false,
    dualApprovalId,
    userId,
    fileName: batch.file_name,
    isReversible: false,
    hasReversal: false,
  };
};

/** Display statuses that allow cancellation for ACH file imports. */
const ACH_CANCELABLE_STATUSES = new Set([
  DisplayStatus.pending_approval,
  DisplayStatus.in_progress,
  DisplayStatus.scheduled,
]);

/**
 * Determine whether the cancel option should be shown for a file import item.
 *
 * - ACH: cancellable when status is pending approval, in progress, or scheduled.
 * - Wire: cancellable only when pending approval, the batch has a dual approval,
 *   and the current user is the requester (same logic as individual wire cancel
 *   in paymentActivity/helpers.ts).
 */
export const showFileImportCancelOption = (
  item: FormattedFileImportItem,
  options?: { currentUserUuid?: API.UserId },
): boolean => {
  const { type, displayStatus, dualApprovalId, userId } = item;

  if (type === "ach") {
    return ACH_CANCELABLE_STATUSES.has(displayStatus);
  }

  if (type === "wire") {
    const isRequester =
      Boolean(options?.currentUserUuid) &&
      Boolean(userId) &&
      options?.currentUserUuid === userId;
    return (
      displayStatus === DisplayStatus.pending_approval &&
      dualApprovalId !== null &&
      isRequester
    );
  }

  return false;
};
